'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { CONTACT_IMAGES } from '@/lib/mediaConfig';

export default function ContactSection() {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Backend integration point: POST /api/contact
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="contact" className="py-24 lg:py-32">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">Contact Us</p>
          <h2 className="section-title text-foreground mb-4">
            Get in <span className="gradient-gold-text">Touch</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
          {/* Left: contact info + office image */}
          <div className="lg:col-span-2 space-y-5">
            {/* Office image */}
            <div className="relative h-48 rounded-2xl overflow-hidden mb-6">
              <Image
                src={CONTACT_IMAGES.office.src}
                alt={CONTACT_IMAGES.office.alt}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 40vw"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-bold text-sm">Motion Max HQ</p>
                <p className="text-white/70 text-xs">Chennai, Tamil Nadu</p>
              </div>
            </div>

            <div className="glass-card rounded-2xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm mb-1">Email Us</p>
                <p className="text-muted-foreground text-sm">awards@motionmax.in</p>
                <p className="text-muted-foreground text-sm">support@motionmax.in</p>
              </div>
            </div>
            <div className="glass-card rounded-2xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Phone size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm mb-1">Call Us</p>
                <p className="text-muted-foreground text-sm">+91 98400 12345</p>
                <p className="text-muted-foreground text-xs text-muted-foreground/60">Mon–Sat, 10am–6pm IST</p>
              </div>
            </div>
            <div className="glass-card rounded-2xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin size={18} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm mb-1">Office</p>
                <p className="text-muted-foreground text-sm">42, Anna Salai, Chennai — 600002</p>
                <p className="text-muted-foreground text-sm">Tamil Nadu, India</p>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 glass-card rounded-2xl p-8 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Name</label>
                <input className="input-dark w-full px-4 py-3 rounded-xl text-sm" placeholder="Your full name" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Email</label>
                <input type="email" className="input-dark w-full px-4 py-3 rounded-xl text-sm" placeholder="you@example.com" required />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Subject</label>
              <input className="input-dark w-full px-4 py-3 rounded-xl text-sm" placeholder="How can we help?" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Message</label>
              <textarea className="input-dark w-full px-4 py-3 rounded-xl text-sm resize-none" rows={5} placeholder="Your message..." required />
            </div>
            <button
              type="submit"
              className="btn-primary w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2"
            >
              {sent ? '✓ Message Sent!' : (<><Send size={16} /> Send Message</>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}